import React, { useState } from 'react';
import dayjs from 'dayjs';

import { FormComponent, ModalComponent, UiComponent } from 'components';
import { useIsMobile, usePageOption } from 'hooks';
import { ContentsQuery } from 'queries';
import * as M from 'styles/mobile';
import { theme } from 'styles';
import { ContentType, IContent } from 'types';

const CONTENT_TYPE_LABEL: Record<ContentType, string> = {
  [ContentType.INTERNAL]: '상세페이지',
  [ContentType.EXTERNAL]: '외부페이지',
  [ContentType.YOUTUBE]: 'youtube',
  [ContentType.INSTAGRAM]: 'instagram',
  [ContentType.NONE]: '설정안함',
  [ContentType.EVENT_DETAIL]: '봉사 상세',
};

const mobileType = {
  marginLeft: 4,
  color: theme.color.textMuted,
};

interface Props {
  onSelect: (content: IContent) => void;
  onClose: () => void;
}

/** 연결할 컨텐츠 선택 모달. 봉사 선택(EventSelect)과 같은 흐름이다. */
function ContentSelect({ onSelect, onClose }: Props) {
  const isMobile = useIsMobile();
  const pageState = usePageOption(10);
  const [title, setTitle] = useState('');
  const { data } = ContentsQuery.useGetAll({
    pageType: 'LIST',
    page: pageState.pageOption.page,
    size: pageState.pageOption.size,
    title,
  });

  return (
    <ModalComponent.Portal>
      <ModalComponent.Background>
        <ModalComponent.Modal
          title="컨텐츠 선택"
          handleClose={onClose}
          footer={<></>}
        >
          <div css={{ width: '720px', maxWidth: '100%' }}>
            <FormComponent.Form
              submit={({ keyword }: { keyword?: string }) =>
                setTitle(keyword ?? '')
              }
              css={{
                display: 'flex',
                gap: '0 8px',
                marginBottom: '12px',
              }}
            >
              <FormComponent.SearchInput
                name="keyword"
                placeholder="컨텐츠명으로 검색해주세요"
                css={{ width: '100%' }}
              />
            </FormComponent.Form>

            {isMobile ? (
              <div css={M.list}>
                {(data?.data ?? []).map((content: IContent) => (
                  <button
                    key={content.id}
                    type="button"
                    css={M.listItem}
                    onClick={() => onSelect(content)}
                  >
                    <p>
                      {content.title}
                      <span css={mobileType}>
                        ({CONTENT_TYPE_LABEL[content.type] ?? content.type})
                      </span>
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div css={{ maxHeight: '420px', overflowY: 'auto' }}>
                <UiComponent.Table
                  cols={['ID', '컨텐츠명', '유형', '등록일']}
                  data={data?.data ?? []}
                  onClickRow={onSelect}
                  renderer={{
                    ID: ({ id }: IContent) => <>{id}</>,
                    컨텐츠명: ({ title: contentTitle }: IContent) => (
                      <p css={{ textAlign: 'left' }}>{contentTitle}</p>
                    ),
                    유형: ({ type }: IContent) => (
                      <>{CONTENT_TYPE_LABEL[type] ?? type}</>
                    ),
                    등록일: ({ createdAt }: IContent) => (
                      <>{dayjs(createdAt).format('YYYY-MM-DD')}</>
                    ),
                  }}
                />
              </div>
            )}
            <UiComponent.Pagination
              {...pageState}
              totalPage={data?.totalPage || 0}
            />
          </div>
        </ModalComponent.Modal>
      </ModalComponent.Background>
    </ModalComponent.Portal>
  );
}

export default ContentSelect;
